/**
 * @file dailyArticlesView.ts
 * @description модель отображения блока «Афиша Daily» на главной и смежных страницах.
 *    здесь зафиксированы правила для интерфейса: когда карусель показывается, когда нужен скелетон загрузки
 *    и что делать при повторном заходе на страницу — сначала показать уже загруженные новости из локального кеша,
 *    а свежий список подтянуть в фоне, чтобы блок не пропадал и не «мигал» при возврате назад.
 */

import type { TypeMappingDailyGQLOutput } from '@shared/api/middleware/mappingAPI/gql_daily/type'
import { readDailyArticlesCache } from '@shared/lib/browser-cache/dailyArticlesCache'

import {
  isHealthyDailyArticlesList,
  isValidDailyArticle,
} from '@shared/api/middleware/mappingAPI/gql_daily/helpers/isValidDailyArticle'

export type DailyArticlesState = {
  articles: TypeMappingDailyGQLOutput[] | undefined
  loading: boolean
}

export type DailyArticlesAction =
  | { type: 'apply_cache'; articles: TypeMappingDailyGQLOutput[] }
  | { type: 'prepare_revalidate'; hasCache: boolean }
  | { type: 'revalidate_done'; list: TypeMappingDailyGQLOutput[]; softFail: boolean }
  | { type: 'revalidate_failed'; softFail: boolean }

export const DAILY_SKELETON_KEYS = [
  'daily-skeleton-0',
  'daily-skeleton-1',
  'daily-skeleton-2',
  'daily-skeleton-3',
] as const

export const createInitialDailyArticlesState = (): DailyArticlesState => {
  const cached = readDailyArticlesCache()

  return {
    articles: cached,
    loading: !cached?.length,
  }
}

export const selectDailyCarouselVisible = ({ loading, articles }: DailyArticlesState): boolean =>
  (loading && !articles) || !!articles?.length

export const selectDailyCarouselSkeleton = ({ loading, articles }: DailyArticlesState): boolean =>
  loading && !articles?.length

export const resolveArticlesAfterRevalidate = (
  list: TypeMappingDailyGQLOutput[],
  softFail: boolean,
  cached: TypeMappingDailyGQLOutput[] | undefined,
): TypeMappingDailyGQLOutput[] => {
  const validList = list.filter(isValidDailyArticle)
  const validCached = (cached ?? []).filter(isValidDailyArticle)

  return (
    [
      [isHealthyDailyArticlesList(validList), validList] as const,
      [softFail && isHealthyDailyArticlesList(validCached), validCached] as const,
      [validList.length > 0, validList] as const,
      [true, validCached] as const,
    ].find(([match]) => match)?.[1] ?? validList
  )
}

const applyCacheTransition = (
  _: DailyArticlesState,
  { articles }: Extract<DailyArticlesAction, { type: 'apply_cache' }>,
): DailyArticlesState => ({ articles, loading: false })

const prepareRevalidateTransition = (
  state: DailyArticlesState,
  { hasCache }: Extract<DailyArticlesAction, { type: 'prepare_revalidate' }>,
): DailyArticlesState => ({ ...state, loading: hasCache ? false : true })

const revalidateDoneTransition = (
  state: DailyArticlesState,
  { list, softFail }: Extract<DailyArticlesAction, { type: 'revalidate_done' }>,
): DailyArticlesState => ({
  articles: resolveArticlesAfterRevalidate(list, softFail, state.articles),
  loading: false,
})

const revalidateFailedTransition = (
  state: DailyArticlesState,
  { softFail }: Extract<DailyArticlesAction, { type: 'revalidate_failed' }>,
): DailyArticlesState => ({
  ...state,
  articles: softFail || isHealthyDailyArticlesList(state.articles) ? state.articles : [],
  loading: false,
})

const dailyArticlesTransitions: Record<
  DailyArticlesAction['type'],
  (state: DailyArticlesState, action: DailyArticlesAction) => DailyArticlesState
> = {
  apply_cache: applyCacheTransition,
  prepare_revalidate: prepareRevalidateTransition,
  revalidate_done: revalidateDoneTransition,
  revalidate_failed: revalidateFailedTransition,
}

export const dailyArticlesReducer = (state: DailyArticlesState, action: DailyArticlesAction): DailyArticlesState =>
  dailyArticlesTransitions[action.type](state, action)

export const mapDailyArticleToCardProps = (article: TypeMappingDailyGQLOutput) => ({
  id: article.id,
  title: article.title,
  url: article.url,
  img: article.img[0],
})
